import { useState } from "react";
import { api, downloadFile, apiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/DateInput";
import HelpButton from "@/components/HelpButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAsyncData } from "@/lib/useAsyncData";
import { fmtNumber, fmtDate } from "@/lib/format";
import { Download, Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"];
const SRC_LABEL = { amount: "sumă", cota: "cotă", table: "tabel" };

export default function ScheduleDialog({ contract, open, onOpenChange }) {
  const [dateIn, setDateIn] = useState("");
  const [dateOut, setDateOut] = useState("");
  const [year, setYear] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVals, setEditVals] = useState({});

  const { loading, data, reload } = useAsyncData(async () => {
    const params = {};
    if (dateIn) params.date_in = dateIn;
    if (dateOut) params.date_out = dateOut;
    if (year) params.year = year;
    const res = await api.get(`/leasing/contracts/${contract.id}/schedule`, { params });
    if (!year && res.data.per_year.length) setYear(String(res.data.per_year[0].year));
    return res.data;
  }, [contract.id, dateIn, dateOut, year]);

  const startEdit = (r) => {
    setEditId(r.installment_id);
    setEditVals({
      nr_rata: r.nr_rata, due_date: r.due_date, principal: r.principal, interest: r.interest,
      cota_tva: r.cota_tva ?? "", baza_tva: r.baza_tva ?? "auto", tva_amount: r.tva_amount ?? "",
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/leasing/installments/${editId}`, {
        nr_rata: parseInt(editVals.nr_rata, 10),
        due_date: editVals.due_date,
        principal: parseFloat(editVals.principal),
        interest: parseFloat(editVals.interest),
        cota_tva: editVals.cota_tva === "" ? null : parseFloat(editVals.cota_tva),
        baza_tva: editVals.baza_tva === "auto" ? null : editVals.baza_tva,
        tva_amount: editVals.tva_amount === "" ? null : parseFloat(editVals.tva_amount),
      });
      toast.success("Rată actualizată");
      setEditId(null);
      reload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Eroare la salvare");
    }
  };

  const doExport = async () => {
    let path = `/leasing/contracts/${contract.id}/schedule/export`;
    const qs = [];
    if (dateIn) qs.push(`date_in=${dateIn}`);
    if (dateOut) qs.push(`date_out=${dateOut}`);
    if (qs.length) path += "?" + qs.join("&");
    try {
      await downloadFile(path, `Scadentar_${contract.display_code}.xlsx`);
    } catch (e) {
      toast.error("Export eșuat");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="contract-schedule-detail-table">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span>Scadențar {contract.display_code}
              <span className="text-sm font-normal text-slate-500 ml-2">
                {data ? `${fmtDate(data.contract.start_date)} → ${fmtDate(data.contract.end_date)}` : ""}
              </span>
            </span>
            <HelpButton anchor="contracte" />
          </DialogTitle>
          <DialogDescription className="sr-only">Scadențar contract cu rate, TVA și totaluri.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 pb-3 border-b">
          <div className="space-y-1">
            <Label className="text-xs uppercase text-slate-500">Data în</Label>
            <DateInput value={dateIn} onChange={setDateIn} data-testid="schedule-date-in" className="w-40" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-slate-500">Data ieșire</Label>
            <DateInput value={dateOut} onChange={setDateOut} data-testid="schedule-date-out" className="w-40" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-slate-500">An (principal lunar)</Label>
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="schedule-year" className="w-28" />
          </div>
          {loading && <span className="flex items-center gap-1 text-xs text-slate-400 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> …</span>}
          <Button onClick={doExport} variant="outline" className="gap-2 ml-auto" data-testid="export-schedule-btn">
            <Download className="h-4 w-4" /> Export
          </Button>
        </div>

        {data && (
          <div className="overflow-auto flex-1 space-y-5 pt-2">
            {(dateIn && dateOut) && (
              <div className="flex flex-wrap gap-4 text-sm bg-slate-50 rounded p-3">
                <span>Total principal perioadă: <b className="tabular">{fmtNumber(data.totals_window.principal)}</b></span>
                <span>Total dobândă perioadă: <b className="tabular">{fmtNumber(data.totals_window.interest)}</b></span>
                <span>Total perioadă: <b className="tabular">{fmtNumber(data.totals_window.total)}</b></span>
              </div>
            )}

            <table className="w-full text-sm jh-table">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2">Nr</th>
                  <th className="text-left px-3 py-2">An</th>
                  <th className="text-left px-3 py-2">Scadență</th>
                  <th className="text-right px-3 py-2">Principal</th>
                  <th className="text-right px-3 py-2">Dobândă</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-left px-3 py-2">Bază TVA</th>
                  <th className="text-right px-3 py-2">TVA</th>
                  <th className="text-right px-3 py-2">Total incl. TVA</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const editing = editId === r.installment_id;
                  return (
                    <tr key={r.installment_id} className="hover:bg-slate-50">
                      {editing ? (
                        <>
                          <td className="px-2 py-1"><Input value={editVals.nr_rata} onChange={(e) => setEditVals({ ...editVals, nr_rata: e.target.value })} className="w-14 h-8" /></td>
                          <td className="px-2 py-1">{r.year}</td>
                          <td className="px-2 py-1"><DateInput value={editVals.due_date} onChange={(v) => setEditVals({ ...editVals, due_date: v })} className="w-32 h-8" /></td>
                          <td className="px-2 py-1"><Input value={editVals.principal} onChange={(e) => setEditVals({ ...editVals, principal: e.target.value })} className="w-24 h-8 text-right" /></td>
                          <td className="px-2 py-1"><Input value={editVals.interest} onChange={(e) => setEditVals({ ...editVals, interest: e.target.value })} className="w-24 h-8 text-right" /></td>
                          <td className="px-3 py-1 text-right tabular text-slate-400">—</td>
                          <td className="px-2 py-1">
                            <Select value={editVals.baza_tva} onValueChange={(v) => setEditVals({ ...editVals, baza_tva: v })}>
                              <SelectTrigger className="w-28 h-8" data-testid="edit-baza-tva"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">Auto (finanțator)</SelectItem>
                                <SelectItem value="dobanda">Dobândă</SelectItem>
                                <SelectItem value="total">Total</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1">
                            <div className="flex gap-1">
                              <Input value={editVals.tva_amount} onChange={(e) => setEditVals({ ...editVals, tva_amount: e.target.value })} className="w-20 h-8 text-right" placeholder="Sumă" data-testid="edit-tva-amount" />
                              <Input value={editVals.cota_tva} onChange={(e) => setEditVals({ ...editVals, cota_tva: e.target.value })} className="w-14 h-8 text-right" placeholder="Cotă%" data-testid="edit-cota-tva" />
                            </div>
                          </td>
                          <td className="px-3 py-1 text-right tabular text-slate-400">—</td>
                          <td className="px-2 py-1 flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={saveEdit} data-testid="save-installment-btn"><Check className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-1.5 tabular">{r.nr_rata}</td>
                          <td className="px-3 py-1.5 tabular">{r.year}</td>
                          <td className="px-3 py-1.5 tabular">{fmtDate(r.due_date)}</td>
                          <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.principal)}</td>
                          <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.interest)}</td>
                          <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.total)}</td>
                          <td className="px-3 py-1.5">
                            {r.vat_base_kind === "dobanda" ? "Dobândă" : "Total"}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular">
                            {fmtNumber(r.vat)}
                            <span className="text-xs text-slate-400 ml-1">
                              {r.vat_rate != null ? `${fmtNumber(r.vat_rate, 0)}%` : ""} ({SRC_LABEL[r.vat_source]})
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.total_incl_vat)}</td>
                          <td className="px-2 py-1.5 text-right">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" onClick={() => startEdit(r)} data-testid={`edit-installment-${r.nr_rata}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <h4 className="text-sm font-semibold mb-2">Principal / dobândă pe an</h4>
                <table className="w-full text-sm jh-table border rounded">
                  <thead className="bg-slate-100"><tr><th className="text-left px-3 py-2">An</th><th className="text-right px-3 py-2">Principal</th><th className="text-right px-3 py-2">Dobândă</th></tr></thead>
                  <tbody>
                    {data.per_year.map((y) => (
                      <tr key={y.year} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 tabular">{y.year}</td>
                        <td className="px-3 py-1.5 text-right tabular">{fmtNumber(y.principal)}</td>
                        <td className="px-3 py-1.5 text-right tabular">{fmtNumber(y.interest)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">Principal lunar {year ? `(${year})` : ""}</h4>
                <table className="w-full text-sm jh-table border rounded">
                  <thead className="bg-slate-100"><tr><th className="text-left px-3 py-2">Lună</th><th className="text-right px-3 py-2">Principal</th></tr></thead>
                  <tbody>
                    {data.monthly_principal.map((v, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5">{MONTHS[i]}</td>
                        <td className="px-3 py-1.5 text-right tabular">{fmtNumber(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
